import { Formik } from 'formik';
import { CheckboxCard, SectionLegend, StepShell } from 'components/Profile';
import { blueCheckboxCardClass, blueCheckboxClass, choiceBtnUnselected } from 'components/Profile/helper';
import { getInitialValues, getValidationSchema, allAccepted, setAllConsents, isSigned, pendingForms, signedForms } from './helper';
import ConsentModal from './Modal';
import Signed from './Signed';
import Documents from './Documents';
import { getPatient } from 'library/auth';
import { connect } from 'react-redux';
import { ProfileActions } from 'store/actions';
import toast from 'react-hot-toast';
import { useOutletContext } from 'react-router-dom';
import { useState } from 'react';
import { FileText } from 'lucide-react';

const enhancer = connect(null, { ...ProfileActions })

const Form = ({ createConsent }) => {
  const { sectionData, mergeSection, goToNextStep }: any = useOutletContext();
  const [activeConsent, setActiveConsent] = useState(null);
  const consentForms = sectionData.consent || [];
  const signed = isSigned(consentForms);
  const pending = pendingForms(consentForms);
  const signedDocuments = signedForms(consentForms);
  const patientName = getPatient()?.full_name;

  const handleSubmit = async () => {
    try {
      const consentFormIds = consentForms.map(({ id }) => id);

      const { data } = await createConsent({ patient: { consent_form_ids: consentFormIds } });

      toast.success('Consent forms saved successfully');
      mergeSection('consent', data);
      goToNextStep('consent');

    } catch (error: any) {
      toast.error(error.errors || 'Failed to save consent forms. Please try again later.');
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(pending)}
      validationSchema={getValidationSchema(pending)}
      onSubmit={handleSubmit}
    >
      {({ values, setValues, isSubmitting }) => {
        const setConsent = (id, checked) => {
          const consents = { ...values.consents, [id]: checked };
          setValues({ consents, accept_all: allAccepted(consents, pending) });
        }

        return (
          <StepShell stepId="consent" isSubmitting={isSubmitting}>
            {signed ? (
              <Signed consentForms={consentForms} patientName={patientName} onView={setActiveConsent} />
            ) : (
              <>
                {pending.length > 1 && (
                  <CheckboxCard
                    name="accept_all"
                    label={`I agree to all ${pending.length} forms`}
                    description="Standard privacy, treatment and billing terms. Read any of them below."
                    className={`${blueCheckboxCardClass} mb-5`}
                    checkboxClassName={blueCheckboxClass}
                    labelClassName="text-[15px] font-bold text-secondary-900"
                    onChange={({ target: { checked } }) => setValues({
                      accept_all: checked,
                      consents: setAllConsents(pending, checked)
                    })}
                  />
                )}

                <SectionLegend>Required agreements</SectionLegend>

                <div className="mb-5 space-y-3">
                  {pending.map((consentForm) => {
                    const { id, attributes: { title, description } } = consentForm;

                    return (
                      <CheckboxCard
                        key={id}
                        name={`consents.${id}`}
                        label={title}
                        description={description}
                        className={blueCheckboxCardClass}
                        checkboxClassName={blueCheckboxClass}
                        labelClassName="font-bold text-secondary-900"
                        onChange={({ target: { checked } }) => setConsent(id, checked)}
                        action={
                          <button
                            type="button"
                            onClick={() => setActiveConsent(consentForm)}
                            className={`btn btn-sm h-9 gap-2 rounded-[10px] font-semibold ${choiceBtnUnselected}`}
                          >
                            <FileText size={15} strokeWidth={2.2} /> View consent
                          </button>
                        }
                      />
                    )
                  })}
                </div>

                {signedDocuments.length > 0 && (
                  <>
                    <SectionLegend>Your signed documents</SectionLegend>

                    <Documents consentForms={signedDocuments} onView={setActiveConsent} />
                  </>
                )}
              </>
            )}

            {activeConsent && (
              <ConsentModal
                consentForm={activeConsent}
                onClose={() => setActiveConsent(null)}
                onAgree={() => {
                  setConsent(activeConsent.id, true);
                  setActiveConsent(null);
                }}
              />
            )}
          </StepShell>
        )
      }}
    </Formik>
  )
}

export default enhancer(Form);
